import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  CloudDownload,
  ImageSearch,
  CompareArrows,
  AddLink,
  LinkOff,
} from "@mui/icons-material";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  Tooltip,
  FormControlLabel,
  Checkbox,
  IconButton,
  Button,
  Box,
  MenuItem,
  TextField,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Item,
  ItemDetails,
  ItemList,
  OrgConfigModel,
  getItemTypeOption,
  patchSubmissionAttachment,
  postSubmissionAttachmentExportStatus,
} from "@rubi/api-generated";
import { colors } from "@rubi/ui";
import { toast } from "@rubi/ui";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import {
  ReactElementFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

import AttachmentPreviewModal from "@/common/AttachmentPreviewModal";
import ImageSearchModal from "@/components/alerts/Search/ImageSearchModal";
import { OnlineReportingContext } from "@/components/online-reporting/context";
import { useImageMatch } from "@/contexts/ImageMatchContext";

import AttachmentMatchIndicator from "./AttachmentMatchIndicator";
import { buildDefaultTitle } from "./attachmentTitleHelpers";
import { tQuestion } from "./i18nQuestion";

export interface FilePreviewConfig {
  showExportCheckbox: boolean;
  enableAttachmentTitle: boolean;
  attachmentTypeItems: Item[];
}

const NO_ATTACHMENT_TYPE_ITEMS: Item[] = [];

const DEFAULT_FILE_PREVIEW_CONFIG: FilePreviewConfig = {
  showExportCheckbox: false,
  enableAttachmentTitle: false,
  attachmentTypeItems: NO_ATTACHMENT_TYPE_ITEMS,
};

// The file preview is instantiated by SurveyJS, outside the React tree that owns
// the org config, so it reads its configuration from context rather than props.
export const FilePreviewConfigContext = createContext<FilePreviewConfig>(
  DEFAULT_FILE_PREVIEW_CONFIG,
);

// Resolves the org-level attachment settings the preview needs, fetching the
// attachment-type list only when its configured id changes.
export function useFilePreviewConfig(
  serviceConfig: OrgConfigModel | null | undefined,
  showExportCheckbox: boolean,
): FilePreviewConfig {
  const enableAttachmentTitle =
    serviceConfig?.metadata?.enable_attachment_title ?? false;
  const listId =
    serviceConfig?.metadata?.attachment_type_item_type_option_id ?? null;
  const [attachmentTypeItems, setAttachmentTypeItems] = useState<Item[]>(
    NO_ATTACHMENT_TYPE_ITEMS,
  );

  useEffect(() => {
    if (!listId) {
      setAttachmentTypeItems(NO_ATTACHMENT_TYPE_ITEMS);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const option = await getItemTypeOption({
          path: { item_type_option_id: listId },
        });
        if (cancelled) return;
        setAttachmentTypeItems(
          (option.data?.items as ItemList | undefined)?.items ??
            NO_ATTACHMENT_TYPE_ITEMS,
        );
      } catch (err) {
        console.error("Failed to load attachment type options:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [listId]);

  return useMemo(
    () => ({ showExportCheckbox, enableAttachmentTitle, attachmentTypeItems }),
    [showExportCheckbox, enableAttachmentTitle, attachmentTypeItems],
  );
}

function withParams(Component) {
  const WrappedComponent = (props: any) => {
    const params = useParams();
    const navigate = useNavigate();
    const context = useContext(OnlineReportingContext);
    const imageMatch = useImageMatch();
    const filePreviewConfig = useContext(FilePreviewConfigContext);
    return (
      <Component
        {...props}
        {...filePreviewConfig}
        params={params}
        navigate={navigate}
        context={context}
        imageMatch={imageMatch}
      />
    );
  };
  WrappedComponent.displayName = `withParams(${Component.displayName || Component.name || "Component"})`;
  return WrappedComponent;
}

function getPreviewMedia(fileItem: any): {
  mediaSrc: string;
  mediaType: "pdf" | "image" | "video";
} {
  const fileType = fileItem.type ?? "";

  if (fileType.includes("application/pdf")) {
    return { mediaSrc: fileItem.content, mediaType: "pdf" };
  }
  if (fileType.includes("image")) {
    return { mediaSrc: fileItem.content, mediaType: "image" };
  }
  if (fileType.includes("video")) {
    return { mediaSrc: fileItem.content, mediaType: "video" };
  }
  if (fileItem.pdf_url) {
    return { mediaSrc: fileItem.pdf_url, mediaType: "pdf" };
  }
  return { mediaSrc: fileItem.content, mediaType: "video" };
}

class FilePreviewComponent extends SurveyQuestionElementBase {
  constructor(props: any) {
    super(props);
    this.state = {
      question: props.question,
      isImageSearchModalOpen: false,
      selectedFileItem: null,
      previewFileItem: null,
      isIntelApp:
        window.location.pathname.includes("/rubiintel/") &&
        !window.location.pathname.endsWith("/edit"),
    };

    this.applySingleItemPaging(props.question);
  }

  componentDidMount() {
    super.componentDidMount();
    this.backfillTitlesIfEnabled();
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    super.componentDidUpdate(prevProps, prevState);
    this.applySingleItemPaging(this.props.question);
    this.backfillTitlesIfEnabled();
  }

  // Declared as prototype methods, not class fields, so the constructor can call
  // them.

  // Show one attachment per page so SurveyJS's built-in navigator pages through
  // them one at a time. Only write when the value differs — pageSize notifies on
  // set, and survey-core recomputes it from measured widths in
  // processResponsiveness, so an unconditional write ping-pongs with it.
  applySingleItemPaging(question: any) {
    if (!question || !question.isReadOnly) return;
    if (!(question.value?.length > 1)) return;
    if (question.pageSize === 1) return;
    question.pageSize = 1;
  }

  backfillTitlesIfEnabled() {
    if (!this.filePreviewConfig.enableAttachmentTitle) return;
    this.backfillDefaultTitles();
  }

  // SurveyJS constructs this component with untyped props; the attachment
  // settings come from FilePreviewConfigContext via withParams.
  get filePreviewConfig(): FilePreviewConfig {
    return this.props as unknown as FilePreviewConfig;
  }

  backfillDefaultTitles = async () => {
    const question = this.props.question;
    if (!question?.value || !Array.isArray(question.value)) return;
    const updates: { item: any; title: string }[] = [];
    for (const item of question.value) {
      if (!item?.id || !item?.submission_id) continue;
      if (item.title) continue;
      const defaultTitle = buildDefaultTitle(item);
      if (!defaultTitle) continue;
      item.title = defaultTitle;
      updates.push({ item, title: defaultTitle });
    }
    if (updates.length === 0) return;
    this.setState({});
    await Promise.all(
      updates.map(({ item, title }) => this.patchAttachment(item, { title })),
    );
  };

  itemCode = (item: Item): string =>
    typeof item.value === "string" ? item.value : (item.value?.code ?? "");

  itemToItemDetails = (item: Item): ItemDetails => {
    if (item.rmsDetails) return item.rmsDetails;
    const code = this.itemCode(item);
    return { code, item_code: code, label: item.label, item_name: item.label };
  };

  patchAttachment = async (
    fileItem: any,
    body: {
      title?: string;
      description?: string;
      attachment_type?: ItemDetails;
    },
  ) => {
    try {
      await patchSubmissionAttachment({
        path: {
          submission_id: fileItem.submission_id,
          attachment_id: fileItem.id,
        },
        body,
      });
    } catch (error) {
      console.error("Failed to update attachment:", error);
      toast.error(tQuestion("customQuestionFile.failedSaveChanges"));
    }
  };

  updateQuestionValue = (fileItem: any, patch: Record<string, any>) => {
    const question = this.props.question;
    if (!question?.value) return;
    const target = question.value.find((item: any) => item.id === fileItem.id);
    if (target) Object.assign(target, patch);
    Object.assign(fileItem, patch);
  };

  handleTitleBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    fileItem: any,
  ) => {
    const next = event.target.value;
    const current = fileItem.title ?? "";
    if (next === current) return;
    this.updateQuestionValue(fileItem, { title: next });
    this.patchAttachment(fileItem, { title: next });
  };

  handleAttachmentTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    fileItem: any,
  ) => {
    const code = event.target.value;
    const chosen = this.filePreviewConfig.attachmentTypeItems.find(
      (i) => this.itemCode(i) === code,
    );
    if (!chosen) return;
    const details = this.itemToItemDetails(chosen);
    this.updateQuestionValue(fileItem, { attachment_type: details });
    this.setState({});
    this.patchAttachment(fileItem, { attachment_type: details });
  };

  handleExportStatusChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fileItem: any,
  ) => {
    const includeInExport = event.target.checked;

    try {
      await postSubmissionAttachmentExportStatus({
        path: {
          submission_id: fileItem.submission_id,
          attachment_id: fileItem.id,
        },
        body: { include_in_export: includeInExport },
      });
    } catch (error) {
      console.error("Failed to update export status:", error);
      toast.error(tQuestion("enhancedFileUpload.exportStatusFailed"));
    }
    // Update the item in the question's value array in place so the UI reflects the change
    const question = this.props.question;
    if (question?.value) {
      const target = question.value.find(
        (item: any) => item.id === fileItem.id,
      );
      if (target) {
        target.include_in_export = includeInExport;
      }
    }
    this.setState({});
  };
  downloadFile = async (fileItem) => {
    try {
      const data = await axios.get(fileItem.content, {
        responseType: "blob",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          Expires: "0",
        },
      });

      // Create a URL for the blob
      const blobUrl = window.URL.createObjectURL(data.data);

      // Create and trigger download link
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileItem.name;
      document.body.appendChild(a);
      a.click();

      // Clean up
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download file:", error);
      toast.error(tQuestion("enhancedFileUpload.downloadFileFailed"));
    }
  };

  // Builds a distinct, non-colliding name for an edited image so it is stored
  // as an additional attachment rather than overwriting the original.
  buildEditedFileName = (originalName: string): string => {
    const safeName = originalName || "image.jpg";
    const dot = safeName.lastIndexOf(".");
    const base = dot > 0 ? safeName.slice(0, dot) : safeName;
    // The editor always exports JPEG.
    const existingNames = new Set(
      ((this.props.question?.value as any[]) || [])
        .map((item: any) => item?.name)
        .filter(Boolean) as string[],
    );
    let candidate = `${base}-edited.jpg`;
    let counter = 2;
    while (existingNames.has(candidate)) {
      candidate = `${base}-edited-${counter}.jpg`;
      counter += 1;
    }
    return candidate;
  };

  // Uploads an edited image as an additional attachment by reusing the same
  // survey.onUploadFiles pathway a user goes through to add a new attachment.
  handleEditSave = (editedFile: File, originalFileItem: any) => {
    const question = this.props.question;
    const survey = question?.survey as any;
    if (!survey?.onUploadFiles) {
      toast.error("Unable to upload the edited image.");
      return;
    }

    const editedName = this.buildEditedFileName(
      originalFileItem?.name || editedFile.name,
    );
    const fileToUpload = new File([editedFile], editedName, {
      type: editedFile.type || "image/jpeg",
    });

    toast.info("Uploading edited image…");

    const uploadOptions = {
      files: [fileToUpload],
      name: question.name,
      question,
      onProgress: () => {},
      callback: (returnValues: any[], errorValues: string[]) => {
        if (errorValues && errorValues.length > 0) {
          toast.error("Failed to upload the edited image.");
          return;
        }
        const uploaded = returnValues?.[0];
        if (!uploaded) {
          toast.error("Failed to upload the edited image.");
          return;
        }

        const newItem = {
          ...uploaded,
          name: editedName,
          type: fileToUpload.type,
          content: uploaded.content,
          submission_id:
            uploaded.submission_id ?? originalFileItem?.submission_id,
        };

        const currentValue = (question.value || []) as any[];
        question.value = [...currentValue, newItem];

        // Surface the new image in the preview and re-render the file list.
        this.setState({ previewFileItem: newItem });
        toast.success("Edited image added as a new attachment.");
      },
    };

    // Invoke the registered upload handler(s) — identical to how
    // EnhancedFileUploadQuestion triggers an additional-attachment upload.
    const handlers = survey.onUploadFiles?.handlers || [];
    if (handlers.length > 0) {
      handlers.forEach((handler: (sender: any, options: any) => void) => {
        try {
          handler(survey, uploadOptions);
        } catch (error) {
          console.error("Error calling upload handler:", error);
          toast.error("Failed to upload the edited image.");
        }
      });
    } else if (typeof survey.onUploadFiles.fire === "function") {
      survey.onUploadFiles.fire(survey, uploadOptions);
    } else {
      toast.error("Unable to upload the edited image.");
    }
  };

  handleFindSimilar = (fileItem: any) => {
    this.setState({
      isImageSearchModalOpen: true,
      selectedFileItem: fileItem,
    });
  };

  handleImageSearchConfirm = () => {
    const { selectedFileItem } = this.state;
    if (!selectedFileItem) return;

    // Get context from props or use a hook wrapper
    const context = (this.props as any).context;
    if (!context) {
      toast.error(tQuestion("customQuestionFile.imageSearchFailed"));
      return;
    }

    const {
      setImageAttachmentId,
      setImageSubmissionId,
      setImageSearchID,
      setSearchView,
      setCurrentSearchImageData,
    } = context;

    const params = (this.props as any).params;
    const orgId = params?.orgId;

    if (!orgId) {
      toast.error(tQuestion("customQuestionFile.orgUnavailable"));
      return;
    }

    setImageSearchID(null);
    setImageAttachmentId(selectedFileItem.id);
    setImageSubmissionId(selectedFileItem.submission_id);
    setCurrentSearchImageData(selectedFileItem.content);

    setSearchView("card");
  };

  handleImageSearchCancel = () => {
    this.setState({
      isImageSearchModalOpen: false,
      selectedFileItem: null,
    });
  };

  handleImageSearchClear = () => {
    const context = (this.props as any).context;

    if (context) {
      context.setImageAttachmentId(null);
      context.setImageSubmissionId(null);
      context.setCurrentSearchImageData(null);
      context.setImageSearchID(null);
    }
    // Clear the local state as well
    this.setState({
      selectedFileItem: null,
    });
  };

  handleStartMatch = (fileItem: any) => {
    const imageMatch = (this.props as any).imageMatch;
    if (!imageMatch) return;

    imageMatch.startMatch({
      submissionId: fileItem.submission_id,
      attachmentId: fileItem.id,
      filename: fileItem.name,
      contentType: fileItem.type,
      presignedUrl: fileItem.content,
    });
  };

  handleToggleMatchTarget = (fileItem: any) => {
    const imageMatch = (this.props as any).imageMatch;
    if (!imageMatch) return;

    if (imageMatch.isTargetSelected(fileItem.id)) {
      imageMatch.removeTarget(fileItem.id);
    } else {
      imageMatch.addTarget({
        submissionId: fileItem.submission_id,
        attachmentId: fileItem.id,
        filename: fileItem.name,
        contentType: fileItem.type,
        presignedUrl: fileItem.content,
      });
    }
  };

  renderFileItem = (
    fileItem: any,
    isMatchInProgress: boolean,
    imageMatch: any,
  ) => {
    const isImage = fileItem.type?.includes("image");
    const isCurrentSource = imageMatch?.isSource(fileItem.id) ?? false;
    const isCurrentTarget = imageMatch?.isTargetSelected(fileItem.id) ?? false;

    return (
      <div
        key={fileItem.name}
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          border: isCurrentSource
            ? "2px solid var(--primary-dark)"
            : isCurrentTarget
              ? "2px solid var(--success-light)"
              : "2px solid transparent",
          borderRadius: "var(--cr-radius, 4px)",
        }}
      >
        {isCurrentSource && (
          <Box
            sx={{
              backgroundColor: colors.primary.dark,
              color: colors.common.white,
              textAlign: "center",
              py: 0.25,
              fontSize: "11px",
              fontWeight: 600,
            }}
          >
            {tQuestion("customQuestionFile.matchSource")}
          </Box>
        )}
        {isCurrentTarget && (
          <Box
            sx={{
              backgroundColor: colors.success.light,
              color: colors.common.white,
              textAlign: "center",
              py: 0.25,
              fontSize: "11px",
              fontWeight: 600,
            }}
          >
            {tQuestion("customQuestionFile.matchTarget")}
          </Box>
        )}
        <div
          style={{
            padding: "6px",
            position: "relative",
            opacity: 1,
            transition: "opacity 0.2s ease",
          }}
          className="attachment-controls"
        >
          {this.filePreviewConfig.showExportCheckbox && (
            <Tooltip
              title={tQuestion("enhancedFileUpload.rmsExportTooltip")}
              arrow
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fileItem.include_in_export}
                    onChange={(e) => this.handleExportStatusChange(e, fileItem)}
                    color="primary"
                  />
                }
                label={tQuestion("enhancedFileUpload.rmsExportLabel")}
              />
            </Tooltip>
          )}
          {isImage && this.state.isIntelApp && (
            <Tooltip
              title={tQuestion("customQuestionFile.findSimilar")}
              placement="right"
            >
              <IconButton
                onClick={() => this.handleFindSimilar(fileItem)}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: isImage ? 50 : 10,
                  color: colors.common.white,
                  fontSize: "40px",
                  backgroundColor: alpha(colors.common.white, 0.5),
                  zIndex: 1,
                }}
                disableRipple
                size="small"
              >
                <ImageSearch color="secondary" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip
            title={tQuestion("customQuestionFile.download")}
            placement="right"
          >
            <IconButton
              onClick={() => this.downloadFile(fileItem)}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                color: colors.common.white,
                fontSize: "40px",
                backgroundColor: alpha(colors.common.white, 0.5),
                zIndex: 1,
              }}
              disableRipple
              size="small"
            >
              <CloudDownload color="secondary" />
            </IconButton>
          </Tooltip>
          {isImage && this.state.isIntelApp && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                left: 10,
                zIndex: 1,
                display: "flex",
                gap: 0.5,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {fileItem.id && fileItem.submission_id && (
                <AttachmentMatchIndicator
                  attachmentId={fileItem.id}
                  submissionId={fileItem.submission_id}
                />
              )}
              {!isMatchInProgress && !isCurrentSource && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CompareArrows sx={{ fontSize: 16 }} />}
                  onClick={() => this.handleStartMatch(fileItem)}
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    backgroundColor: alpha(colors.common.white, 0.85),
                    color: colors.primary.dark,
                    "&:hover": {
                      backgroundColor: alpha(colors.common.white, 1),
                    },
                  }}
                >
                  {tQuestion("customQuestionFile.startMatch")}
                </Button>
              )}
              {isMatchInProgress && !isCurrentSource && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={
                    isCurrentTarget ? (
                      <LinkOff sx={{ fontSize: 16 }} />
                    ) : (
                      <AddLink sx={{ fontSize: 16 }} />
                    )
                  }
                  onClick={() => this.handleToggleMatchTarget(fileItem)}
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    ...(isCurrentTarget
                      ? {
                          backgroundColor: colors.success.light,
                          color: colors.common.white,
                          "&:hover": { backgroundColor: colors.success.main },
                        }
                      : {
                          backgroundColor: alpha(colors.common.white, 0.85),
                          color: colors.success.light,
                          "&:hover": {
                            backgroundColor: alpha(colors.common.white, 1),
                          },
                        }),
                  }}
                >
                  {isCurrentTarget
                    ? tQuestion("customQuestionFile.removeFromMatch")
                    : tQuestion("customQuestionFile.includeInMatch")}
                </Button>
              )}
            </Box>
          )}
          {this.renderAttachmentMetadata(fileItem)}
        </div>
        {fileItem.type?.includes("video") && (
          <div
            className="sd-file__video-wrapper"
            style={{ display: "flex", justifyContent: "center" }}
          >
            <video
              src={fileItem.content}
              controls
              playsInline
              style={{
                display: "block",
                maxWidth: "100%",
                maxHeight: "25rem",
                width: "auto",
                height: "auto",
                objectFit: "contain",
              }}
              className="sd-file__video"
            />
          </div>
        )}
        <div
          style={{
            height: "100%",
          }}
        >
          {isImage && (
            <div
              className="sd-file__image-wrapper"
              style={{ display: "flex", justifyContent: "center" }}
            >
              <img
                onClick={() => this.setState({ previewFileItem: fileItem })}
                src={
                  // Use the full image unless it's a HEIC/HEIF that browsers can't render
                  fileItem.type?.includes("heic") ||
                  fileItem.type?.includes("heif") ||
                  fileItem.name?.toLowerCase().endsWith(".heic") ||
                  fileItem.name?.toLowerCase().endsWith(".heif")
                    ? (fileItem.thumbnail_url ?? fileItem.content)
                    : fileItem.content || fileItem.thumbnail_url
                }
                alt={fileItem.name}
                className="sd-file__image"
                style={{
                  cursor: "pointer",
                  maxWidth: "100%",
                  maxHeight: "250px",
                  objectFit: "contain",
                }}
              />
            </div>
          )}
        </div>
        {!fileItem.type?.includes("image") &&
          !fileItem.type?.includes("video") && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor:
                  fileItem.pdf_url || fileItem.type?.includes("application/pdf")
                    ? "pointer"
                    : "default",
              }}
              onClick={
                fileItem.pdf_url || fileItem.type?.includes("application/pdf")
                  ? () => this.setState({ previewFileItem: fileItem })
                  : undefined
              }
            >
              <DescriptionIcon sx={{ fontSize: 80 }} />
              <p>{fileItem.name}</p>
            </div>
          )}
      </div>
    );
  };

  renderAttachmentMetadata = (fileItem: any) => {
    // if (!fileItem.id || !fileItem.submission_id) return null;
    const { attachmentTypeItems, enableAttachmentTitle } =
      this.filePreviewConfig;
    const storedType = fileItem.attachment_type as
      | ItemDetails
      | null
      | undefined;
    const storedCode = storedType?.code ?? storedType?.item_code ?? "";
    const fallbackCode =
      attachmentTypeItems.length > 0
        ? this.itemCode(attachmentTypeItems[0])
        : "";
    const selectedTypeCode = storedCode || fallbackCode;
    if (!enableAttachmentTitle && attachmentTypeItems.length === 0) return null;
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1,
          marginTop: 1,
          alignItems: "center",
          width: "100%",
          paddingRight: "90px",
        }}
      >
        {enableAttachmentTitle && (
          <TextField
            size="small"
            label={tQuestion("customQuestionFile.title")}
            defaultValue={fileItem.title ?? ""}
            onBlur={(e) => this.handleTitleBlur(e, fileItem)}
            sx={{ flex: 1, minWidth: 0 }}
          />
        )}
        {attachmentTypeItems.length > 0 && (
          <TextField
            size="small"
            select
            label={tQuestion("customQuestionFile.type")}
            value={selectedTypeCode}
            onChange={(e) =>
              this.handleAttachmentTypeChange(
                e as React.ChangeEvent<HTMLInputElement>,
                fileItem,
              )
            }
            sx={{ flex: 1, minWidth: 0 }}
          >
            {attachmentTypeItems.map((item) => {
              const code = this.itemCode(item);
              return (
                <MenuItem key={code} value={code}>
                  {item.label}
                </MenuItem>
              );
            })}
          </TextField>
        )}
      </Box>
    );
  };

  render() {
    const question = this.props.question;
    if (!question || !question.value) {
      return null;
    }

    if (!(typeof question.value.map === "function")) {
      return null;
    }

    const imageMatch = (this.props as any).imageMatch;
    const isMatchInProgress = imageMatch?.isMatchInProgress ?? false;

    // Use SurveyJS's renderedPages to respect its built-in paging
    const renderedPages = question.renderedPages;
    const allItems = question.value;

    // renderedPages items are stripped of custom properties (id, submission_id, etc.)
    // Merge with the full item from question.value by matching on name
    const resolveFileItem = (pageItem: any) => {
      const fullItem = allItems.find((v: any) => v.name === pageItem.name);
      return fullItem
        ? {
            ...pageItem,
            ...fullItem,
            content: pageItem.content || fullItem.content,
          }
        : pageItem;
    };

    return (
      <div className="my-preview-container">
        <div
          className="sd-file__list"
          style={{
            // SurveyJS sizes this list as a 120px strip with overflow hidden,
            // which clips a video's control bar.
            maxHeight: "none",
            height: "auto",
            overflow: "visible",
            padding: 0,
          }}
        >
          {renderedPages && renderedPages.length > 0
            ? renderedPages.map((page: any) =>
                page.items.map((pageItem: any) =>
                  this.renderFileItem(
                    resolveFileItem(pageItem),
                    isMatchInProgress,
                    imageMatch,
                  ),
                ),
              )
            : allItems.map((fileItem: any) =>
                this.renderFileItem(fileItem, isMatchInProgress, imageMatch),
              )}
        </div>
        {this.state.previewFileItem &&
          (() => {
            const {
              mediaSrc,
              mediaType,
            }: { mediaSrc: string; mediaType: "pdf" | "image" | "video" } =
              getPreviewMedia(this.state.previewFileItem);
            const previewIndex = allItems.indexOf(this.state.previewFileItem);
            return (
              <AttachmentPreviewModal
                open={!!this.state.previewFileItem}
                onClose={() => this.setState({ previewFileItem: null })}
                mediaSrc={mediaSrc}
                mediaType={mediaType}
                fileName={this.state.previewFileItem.name}
                onDownload={() => this.downloadFile(this.state.previewFileItem)}
                onEditSave={
                  !(this.props as any).question?.isReadOnly &&
                  this.state.previewFileItem.type?.includes("image")
                    ? (editedFile: File) =>
                        this.handleEditSave(
                          editedFile,
                          this.state.previewFileItem,
                        )
                    : undefined
                }
                currentIndex={previewIndex >= 0 ? previewIndex : undefined}
                totalCount={allItems.length > 1 ? allItems.length : undefined}
                onNavigate={
                  allItems.length > 1
                    ? (newIndex) =>
                        this.setState({ previewFileItem: allItems[newIndex] })
                    : undefined
                }
              />
            );
          })()}
        {this.state.isImageSearchModalOpen && (
          <ImageSearchModal
            open={this.state.isImageSearchModalOpen}
            onClose={this.handleImageSearchCancel}
            onConfirm={this.handleImageSearchConfirm}
            onClear={this.handleImageSearchClear}
            initialImageData={
              (this.props as any).context?.currentSearchImageData ||
              this.state.selectedFileItem?.content ||
              null
            }
          />
        )}
      </div>
    );
  }
}

// Created once: a new component type per call would make React unmount and
// remount the preview on every SurveyJS render instead of updating it.
const FilePreviewWithParams = withParams(FilePreviewComponent);

ReactElementFactory.Instance.registerElement("sv-file-preview", (props) =>
  React.createElement(FilePreviewWithParams, props),
);
